import React, { useState } from 'react';
import { ExternalLink, Play, Tag } from 'lucide-react';
import { portfolio } from '../data/mock';

const PortfolioSection = ({ id }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  const categories = [
    { id: 'all', label: 'Todos os Projetos' },
    { id: 'Design de Produto', label: 'Design de Produto' },
    { id: 'UI/UX Design', label: 'UI/UX Design' },
    { id: 'Experiência Virtual', label: 'Experiência Virtual' },
    { id: 'Identidade Visual', label: 'Identidade Visual' }
  ];

  const filteredProjects = selectedCategory === 'all' 
    ? portfolio 
    : portfolio.filter(project => project.category === selectedCategory);

  const ProjectModal = ({ project, onClose }) => {
    if (!project) return null;

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="glass-card max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="heading-2 text-xl mb-2">{project.title}</h3>
                <span className="text-brand-primary text-sm">{project.category}</span>
              </div>
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            
            {project.videoUrl ? (
              <div className="aspect-video mb-6 rounded-lg overflow-hidden">
                <iframe
                  src={project.videoUrl.replace('youtu.be/', 'youtube.com/embed/').replace('?si=', '?')}
                  title={project.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            ) : (
              <div className="aspect-video mb-6 rounded-lg overflow-hidden">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            <p className="body-medium mb-4">{project.description}</p>
            
            <div className="flex flex-wrap gap-2">
              {project.tags.map((tag, index) => (
                <span 
                  key={index} 
                  className="px-3 py-1 bg-brand-primary/10 text-brand-primary text-xs rounded-full border border-brand-primary/20"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section id={id} className="section-padding">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-16 fade-in-up">
          <div className="inline-flex items-center gap-2 glass-card px-4 py-2 text-sm text-brand-primary border border-brand-primary/20 mb-6">
            <span>Nossos Trabalhos</span>
          </div>
          <h2 className="heading-1 mb-6">
            <span className="text-brand-primary">Portfólio</span> de Projetos
          </h2>
          <div className="w-20 h-1 bg-brand-primary rounded-full mx-auto mb-8"></div>
          <p className="body-large max-w-3xl mx-auto">
            Conheça alguns dos projetos que desenvolvemos para nossos clientes, 
            desde conceito até a implementação final.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full transition-all duration-300 ${
                selectedCategory === category.id
                  ? 'bg-brand-primary text-text-inverse'
                  : 'glass-card border border-brand-primary/20 text-text-secondary hover:text-brand-primary hover:border-brand-primary/40'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filteredProjects.map((project, index) => (
            <div 
              key={project.id}
              className="glass-card group cursor-pointer hover:border-brand-primary/30 transition-all duration-500 overflow-hidden"
              onClick={() => setSelectedProject(project)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Image */}
              <div className="relative aspect-video overflow-hidden">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-bg-card/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-brand-primary">
                        {project.videoUrl ? (
                          <><Play size={16} /> <span className="text-xs">Vídeo</span></>
                        ) : (
                          <><ExternalLink size={16} /> <span className="text-xs">Ver mais</span></>
                        )}
                      </div>
                      <Tag size={16} className="text-brand-primary" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="mb-3">
                  <h3 className="heading-3 text-lg mb-2 group-hover:text-brand-primary transition-colors line-clamp-1">
                    {project.title}
                  </h3>
                  <span className="text-brand-primary text-sm">{project.category}</span>
                </div>
                
                <p className="body-small text-text-secondary leading-relaxed line-clamp-3 mb-4">
                  {project.description}
                </p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {project.tags.slice(0, 3).map((tag, tagIndex) => (
                    <span 
                      key={tagIndex} 
                      className="px-2 py-1 bg-brand-primary/10 text-brand-primary text-xs rounded border border-brand-primary/20"
                    >
                      {tag}
                    </span>
                  ))}
                  {project.tags.length > 3 && (
                    <span className="px-2 py-1 text-text-secondary text-xs">+{project.tags.length - 3}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="glass-card p-8 max-w-2xl mx-auto">
            <h3 className="heading-2 text-2xl mb-4">
              Gostou do que viu? <span className="text-brand-primary">Vamos conversar!</span>
            </h3>
            <p className="body-medium mb-6">
              Cada projeto é único e merece uma abordagem personalizada. 
              Entre em contato para discutirmos suas necessidades.
            </p>
            <button 
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="btn-primary"
            >
              Iniciar Conversa
            </button>
          </div>
        </div>
      </div>

      {/* Project Modal */}
      <ProjectModal 
        project={selectedProject} 
        onClose={() => setSelectedProject(null)} 
      />
    </section>
  );
};

export default PortfolioSection;